import { Checkbox, ColumnLayout, DatePicker, FormField, Input, SpaceBetween } from '@amzn/awsui-components-react';
import { validate, ValidationError } from 'class-validator';
import { useEffect, useState } from 'react';

import { DATE_PLACEHOLDER, toUTCShortDate } from '../../../common/DateDisplay';
import { constants } from '../../../common/Localization';
import { errorLookup, GetErrorText } from '../../../common/validator/ErrorLookup';
import { DEFAULT_APP_CAPACITY, DEFAULT_DB_CAPACITY, DEFAULT_SERVER_CAPACITY, WAVE_END_TIME, WAVE_START_TIME } from './constants';
import { CreateOrEditWaveState, WaveToSave } from './interface';

type Props = {
    createOrEditWaveDetails: CreateOrEditWaveState;
    setCreateOrEditWaveDetails: (details: CreateOrEditWaveState) => void;
};

/**
 * Allow user to create and edit waves
 * When users select a start or end date, the date in created in UTC at a set time: 00:00:00 for start, 23:59:59 for end
 * When dates are displayed to users it is in UTC to be consistent across timezones
 */
function CreateOrEditWave({createOrEditWaveDetails, setCreateOrEditWaveDetails}: Props) {
    const [ getErrorText, setGetErrorText ] = useState<GetErrorText<WaveToSave>>(() => () => '');
    useEffect(() => {
        const waveToEdit = createOrEditWaveDetails.waveToEdit;
        setCreateOrEditWaveDetails({
            waveToEdit,
            waveToSave: {
                name: waveToEdit?.name,
                comment: waveToEdit?.comment,
                startDate: waveToEdit?.startDate,
                endDate: waveToEdit?.endDate,
                applicationCapacity: waveToEdit?.applicationCapacity,
                serverCapacity: waveToEdit?.serverCapacity,
                databaseCapacity: waveToEdit?.databaseCapacity,
                closed: waveToEdit?.closed || false,
            },
            visible: true,
            isInvalid: true,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = async (value: string | Date | number | boolean, property: keyof WaveToSave) => {
        const newWaveToSave = {...createOrEditWaveDetails.waveToSave, [property]: value};
        const validationErrors: ValidationError[] = await validate(Object.assign(new WaveToSave(), newWaveToSave));
        const getErrorTextFn = errorLookup<WaveToSave>(validationErrors);
        setGetErrorText(() => getErrorTextFn);
        const isInvalid = validationErrors.length > 0 || !newWaveToSave.startDate || !newWaveToSave.endDate || !newWaveToSave.name;
        setCreateOrEditWaveDetails({...createOrEditWaveDetails, waveToSave: newWaveToSave, isInvalid });
    };

    /**
     * Convert the string date from polaris date picker to a Date type
     * date picker value is in the format: YYYY-MM-DD
     * @param value 
     * @param property 
     */
    const onDateChanged = (value: string, property: keyof WaveToSave, time: string) => {
        const date: Date = new Date(`${value}${time}`);
        onChange(date, property);
    };

    const waveToSave = createOrEditWaveDetails.waveToSave || {};
    return (
        <SpaceBetween size='s'>
            <h3>{constants.GENERAL_SETTINGS}</h3>
            <ColumnLayout columns={2}>
                <FormField label={constants.WAVE_NAME}
                    errorText={getErrorText('name')}>
                    <Input 
                        placeholder={constants.WAVE_NAME}
                        value={waveToSave.name || ''}
                        onChange={({ detail }) => onChange(detail.value, 'name')}
                    ></Input>
                </FormField>
                <FormField label={constants.COMMENT_OPTIONAL} errorText={getErrorText('comment')}>
                    <Input 
                        placeholder={constants.COMMENTS}
                        value={waveToSave.comment || ''}
                        onChange={({ detail }) => onChange(detail.value, 'comment')}
                    ></Input>
                </FormField>
            </ColumnLayout>
            <ColumnLayout columns={2}>
                <FormField label={constants.START_DATE} errorText={getErrorText('startDate')}>
                    <DatePicker
                        nextMonthAriaLabel={constants.NEXT_MONTH}
                        placeholder={DATE_PLACEHOLDER}
                        previousMonthAriaLabel={constants.PREVIOUS_MONTH}
                        todayAriaLabel={constants.TODAY}
                        // display the date in UTC to be consistent across timezones
                        value={waveToSave.startDate ? toUTCShortDate(new Date(waveToSave.startDate)) : ''}
                        onChange={({ detail }) => onDateChanged(detail.value, 'startDate', WAVE_START_TIME)} />
                </FormField>
                <FormField label={constants.END_DATE} errorText={getErrorText('endDate')}>
                    <DatePicker
                        nextMonthAriaLabel={constants.NEXT_MONTH}
                        placeholder={DATE_PLACEHOLDER}
                        previousMonthAriaLabel={constants.PREVIOUS_MONTH}
                        todayAriaLabel={constants.TODAY}
                        // display the date in UTC to be consistent across timezones
                        value={waveToSave.endDate ? toUTCShortDate(new Date(waveToSave.endDate)) : ''}
                        onChange={({ detail }) => onDateChanged(detail.value, 'endDate', WAVE_END_TIME)} />
                </FormField>
            </ColumnLayout>
            <h3>{constants.WAVE_CAPACITY}</h3>
            <ColumnLayout columns={3}>
                <FormField label={constants.APPLICATIONS} errorText={getErrorText('applicationCapacity')}>
                    <Input
                        placeholder={`${constants.DEFAULT} ${DEFAULT_APP_CAPACITY}`}
                        type='number'
                        value={`${waveToSave.applicationCapacity}` || ''}
                        onChange={({ detail }) => onChange(Number(detail.value), 'applicationCapacity')} />
                </FormField>
                <FormField label={constants.SERVERS} errorText={getErrorText('serverCapacity')}>
                    <Input
                        placeholder={`${constants.DEFAULT} ${DEFAULT_SERVER_CAPACITY}`}
                        type='number'
                        value={`${waveToSave.serverCapacity}` || ''}
                        onChange={({ detail }) => onChange(Number(detail.value), 'serverCapacity')} />
                </FormField>
                <FormField label={constants.DATABASES} errorText={getErrorText('databaseCapacity')}>
                    <Input
                        placeholder={`${constants.DEFAULT} ${DEFAULT_DB_CAPACITY}`}
                        type='number'
                        value={`${waveToSave.databaseCapacity}` || ''}
                        onChange={({ detail }) => onChange(Number(detail.value), 'databaseCapacity')} />
                </FormField>
            </ColumnLayout>
            { createOrEditWaveDetails.waveToEdit ? (
                <Checkbox
                    checked={waveToSave.closed || false}
                    onChange={({ detail }) => onChange(detail.checked, 'closed')}>{constants.CLOSED}
                </Checkbox> 
            ) : null }
        </SpaceBetween>
    );
}

export default CreateOrEditWave;
